import { VehiclesData } from '../types/catalog-market-loader';
import { useFullstoryPageLoadEvent } from '@/fullstory/hooks/use-fullstory-page-load-event';
import getGasSaverCentValue from '@/common/utils/get-gas-saver-cent-value';
import useGetMarketingTiles from '../hooks/use-get-marketing-tiles';
import { useSearchParams } from 'react-router';
import { Fragment } from 'react/jsx-runtime';
import VehicleTileSkeleton from './vehicle-tile-skeleton';
import VehicleTileLink from './vehicle-tile-link';
import { useSavingsCalculatorFetcher } from '../hooks/use-savings-calculator-fetcher';
import { useState } from 'react';
import { Session } from '@/common/types/session';
import SavingsCalculatorV2DrawerDialog from './savings-calculator-v2/savings-calculator-v2-drawer-dialog';
import { AgeRange } from '../types/age-range';
import { CreditScore } from '../types/credit-score';
import { SubscriptionCFA } from '@/account/types/subscription';
import { getMilesPerYearOptions } from '../utils/get-miles-per-year-options';
import { Vehicle } from '../types/vehicle';
import { FSProperties } from '@/fullstory/utils/fullstory-event';
import { useFullstorySendEventOnce } from '@/fullstory/hooks/use-fullstory-send-event-once';

/** Indicates which vehicle images should be loaded first */
const IMAGE_PRIORITY_INDEX_LIMIT = 16;

interface VehicleTilesProps extends VehiclesData {
  market?: string;
  introModalOpen: boolean;
  plan_fullstory_event?: { event_name: string; properties?: FSProperties };
  session: Session;
  PUBLIC_CDN_URL: string;
  INVENTORY_TUTORIAL_ENABLED: boolean;
  showLoadingSkeleton?: boolean;
  ageRanges: AgeRange[];
  creditScores: CreditScore[];
  subscription?: SubscriptionCFA;
  hasChangedSelectedPlan: boolean;
  setHasChangedSelectedPlan: (value: boolean) => void;
  vehicleWaitlistEnabled?: boolean;
  MEMBERSHIP_COST: string;
  MILEAGE_PLAN_OPTIONS: string[];
}

export default function VehicleTiles({
  market,
  vehicles,
  introModalOpen,
  inventory_fullstory_event,
  plan_fullstory_event,
  is_savings_calculator_v2_available,
  session,
  showLoadingSkeleton,
  creditScores,
  ageRanges,
  subscription,
  hasChangedSelectedPlan,
  setHasChangedSelectedPlan,
  PUBLIC_CDN_URL,
  vehicleWaitlistEnabled = false,
  MEMBERSHIP_COST,
  MILEAGE_PLAN_OPTIONS,
}: VehicleTilesProps) {
  const [savingsCalculatorVehicleId, setSavingsCalculatorVehicleId] = useState<number>();
  const [savingsCalculatorOpen, setSavingsCalculatorOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const areIntroModalCartParamsHydrated =
    inventory_fullstory_event?.properties?.credit_range !== 'NOT_DETERMINED' &&
    inventory_fullstory_event?.properties?.credit_range !== undefined;
  const inventoryEventUnlockCondition =
    !introModalOpen && areIntroModalCartParamsHydrated;

  useFullstoryPageLoadEvent(
    plan_fullstory_event?.event_name,
    plan_fullstory_event?.properties
  );

  useFullstorySendEventOnce(
    inventoryEventUnlockCondition,
    inventory_fullstory_event?.event_name,
    inventory_fullstory_event?.properties
  );

  const isSavingsV2DataPresent =
    (is_savings_calculator_v2_available ?? false) && // Is feature available on page?
    (vehicles?.some((vehicle) => vehicle.vehicle_savings_calculator_available) ?? false); // Do some vehicles have savings data?

  const handleSavingsCalculatorClose = () => setSavingsCalculatorOpen(false);

  const gasSaverCents = getGasSaverCentValue(market);

  const marketingTiles = useGetMarketingTiles({
    PUBLIC_CDN_URL,
    vehiclesLength: vehicles?.length,
    vehicleWaitlistEnabled,
    gasSaverCents,
  });

  const { savingsCalculatorData, savingsCalculatorSubmit, fetchSavingsCalculator } =
    useSavingsCalculatorFetcher({
      setSavingsCalculatorVehicleId,
      setSavingsCalculatorOpen,
    });

  return (
    <>
      {vehicles
        ? vehicles.map((vehicle: Vehicle, index) => {
            const matchingMobileTile = marketingTiles.find(
              (mt) => mt.indexes.mobile === index
            );
            const matchingDesktopTile = marketingTiles.find(
              (mt) => mt.indexes.desktop === index
            );

            return (
              <Fragment key={`vehicle-container-${vehicle.id}-${index}`}>
                {showLoadingSkeleton ? (
                  <VehicleTileSkeleton key={`vehicle-tile-skeleton-${index}`} />
                ) : (
                  <VehicleTileLink
                    key={`vehicle-tile-${vehicle.id}-${index}`}
                    market={market}
                    vehicle={vehicle}
                    PUBLIC_CDN_URL={PUBLIC_CDN_URL}
                    inventorySortRank={index + 1}
                    inventorySortOption={searchParams.get('sort_option') || 'popular'}
                    fetchSavingsCalculator={
                      // Only pass fetcher if savings data is available
                      vehicle.vehicle_savings_calculator_available
                        ? fetchSavingsCalculator(vehicle, session, market)
                        : undefined
                    }
                    savingsV2PillId={`savings-pill-${index}`}
                    isSavingsV2DataPresent={isSavingsV2DataPresent}
                    imageLoadPriority={
                      index < IMAGE_PRIORITY_INDEX_LIMIT ? 'high' : 'low'
                    }
                  />
                )}

                {matchingMobileTile ? (
                  <div
                    className="md:hidden"
                    key={`marketing-tile-mobile-${matchingMobileTile.indexes.mobile}`}
                  >
                    {matchingMobileTile.tile}
                  </div>
                ) : null}

                {matchingDesktopTile ? (
                  <div
                    className="hidden md:block"
                    key={`marketing-tile-desktop-${matchingDesktopTile.indexes.desktop}`}
                  >
                    {matchingDesktopTile.tile}
                  </div>
                ) : null}
              </Fragment>
            );
          })
        : null}

      {savingsCalculatorData?.vehicle?.savings_calculator &&
      // Note: This check prevents us from showing stale calculator data from the preview fetch
      savingsCalculatorData.vehicle.id === savingsCalculatorVehicleId ? (
        <SavingsCalculatorV2DrawerDialog
          // This key will force the dialog to remount on key change so that credit and age updates from FSC are reflected here too
          key={`savings-calculator-inventory-${session?.cart?.plan_mileage}-${session?.cart?.credit_score_id}-${session?.cart?.is_over_25}-${session?.cart?.age_range_id}`}
          open={savingsCalculatorOpen}
          handleClose={handleSavingsCalculatorClose}
          creditScores={creditScores}
          ageRanges={ageRanges}
          selectedPlanMileage={session.cart?.plan_mileage}
          selectedCreditScoreId={session.cart?.credit_score_id}
          selectedAge={session.cart?.is_over_25}
          selectedAgeRangeId={session.cart?.age_range_id}
          error={savingsCalculatorData?.errors?.error}
          imageUrl={
            savingsCalculatorData.vehicle.thumbnail_image?.path
              ? `${PUBLIC_CDN_URL}/${savingsCalculatorData.vehicle.thumbnail_image.path}.webp`
              : undefined
          }
          vehicleId={savingsCalculatorData.vehicle.id}
          modelYear={savingsCalculatorData.vehicle.model_year}
          make={savingsCalculatorData.vehicle.make}
          model={savingsCalculatorData.vehicle.model}
          trim={savingsCalculatorData.vehicle.trim}
          odometer={savingsCalculatorData.vehicle.odometer}
          savings={savingsCalculatorData.vehicle.savings_calculator}
          submit={savingsCalculatorSubmit}
          submitIntent="fetch_savings_calculator"
          navigateOnViewCarDetails={true}
          marketId={market}
          membershipCost={MEMBERSHIP_COST}
          hasChangedSelectedPlan={hasChangedSelectedPlan}
          hasChangedSelectedPlanDelegate={() => setHasChangedSelectedPlan(false)}
          openedFrom="inventory"
          cdnUrl={PUBLIC_CDN_URL}
          milesPerYearOptions={
            subscription?.can_swap &&
            subscription?.current_billing_period_summary?.plan_miles
              ? getMilesPerYearOptions() // Show all values
              : getMilesPerYearOptions(MILEAGE_PLAN_OPTIONS ?? undefined) // Show only env mileage plan options
          }
        />
      ) : null}
    </>
  );
}
